import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { t, font, Button, Panel, Badge } from "@realtyflows/ui";
import { requestDocumentUpload, confirmDocumentUpload, getClientDocuments, deleteClientDocument } from "../api";
import { formatBytes } from "../hooks/useStorageUsage";

const DOCUMENT_TYPES = [
  { value: "pre_approval", label: "Pre-Approval" },
  { value: "inspection_report", label: "Inspection Report" },
  { value: "purchase_agreement", label: "Purchase Agreement" },
  { value: "title", label: "Title" },
  { value: "id_verification", label: "ID Verification" },
  { value: "disclosure", label: "Disclosure" },
  { value: "other", label: "Other" },
];

const ACCEPTED_MIMES = [
  "application/pdf",
  "image/jpeg", "image/png", "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const MAX_FILE_SIZE = 52428800; // 50 MB

const TYPE_ICONS = {
  pre_approval: "\u2705",
  inspection_report: "\uD83D\uDD0D",
  purchase_agreement: "\uD83D\uDCDD",
  title: "\uD83C\uDFE0",
  id_verification: "\uD83C\uDD94",
  disclosure: "\uD83D\uDCC4",
  other: "\uD83D\uDCCE",
};

/**
 * Inline delete confirmation modal — rendered via portal to document.body
 * so it's not affected by parent transforms or overflow.
 */
function DeleteModal({ onConfirm, onCancel }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const sidebar = document.querySelector(".rf-sidebar");
    if (sidebar) setLeft(sidebar.offsetWidth);
    else {
      const main = document.querySelector("[data-main-content]");
      if (main) setLeft(main.getBoundingClientRect().left);
    }
  }, []);

  return createPortal(
    <div onClick={onCancel} style={{
      position: "fixed", top: 0, right: 0, bottom: 0, left,
      background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 200, fontFamily: font.body,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: t.surface, borderRadius: 14, width: 360,
        border: `1.5px solid ${t.border}`, boxShadow: "0 16px 48px rgba(0,0,0,0.3)",
        animation: "scaleIn 0.15s ease",
      }}>
        <div style={{ padding: "18px 22px 14px", borderBottom: `1px solid ${t.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
            Delete Document
          </div>
        </div>
        <div style={{ padding: "16px 22px" }}>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: t.textPrimary, fontFamily: font.body }}>
            Are you sure you want to delete this document? It will be recoverable for 30 days.
          </div>
        </div>
        <div style={{ padding: "14px 22px 18px", display: "flex", gap: 10, justifyContent: "flex-end", borderTop: `1px solid ${t.border}` }}>
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={onConfirm}>Delete</Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Client Documents section — upload zone, document list, storage bar.
 * Renders inside the Documents tab of ClientDetail.
 */
export default function ClientDocuments({ clientId }) {
  const [documents, setDocuments] = useState([]);
  const [storageUsed, setStorageUsed] = useState(0);
  const [storageLimit, setStorageLimit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState("pre_approval");
  const [documentLabel, setDocumentLabel] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const fileInputRef = useRef(null);
  const topRef = useRef(null);

  const scrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const fetchDocuments = useCallback(async () => {
    try {
      const data = await getClientDocuments(clientId);
      setDocuments(data.documents || []);
      setStorageUsed(data.storage?.used_bytes || 0);
      setStorageLimit(data.storage?.limit_bytes || 0);
    } catch (err) {
      setError(err.message || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const validateFile = (file) => {
    if (!file) return "No file selected.";
    if (!ACCEPTED_MIMES.includes(file.type)) return "File type not supported. Use PDF, JPEG, PNG, WebP, or Word documents.";
    if (file.size > MAX_FILE_SIZE) return `File is too large. Maximum size is ${formatBytes(MAX_FILE_SIZE)}.`;
    if (file.size + storageUsed > storageLimit) return "Not enough storage. Delete old documents or upgrade your plan.";
    return null;
  };

  const handleFileSelect = (file) => {
    setError("");
    setSuccess("");
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      scrollToTop();
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setError("");
    setSuccess("");
    setUploading(true);
    setUploadProgress(0);

    try {
      // 1. Request signed URL
      const { signedUrl, gcsPath } = await requestDocumentUpload(clientId, {
        fileName: selectedFile.name,
        mimeType: selectedFile.type,
        fileSize: selectedFile.size,
        documentType,
      });

      // 2. PUT file directly to GCS via XMLHttpRequest (for progress tracking)
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", signedUrl);
        xhr.setRequestHeader("Content-Type", selectedFile.type);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed (${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error("Upload failed — check your connection."));
        xhr.send(selectedFile);
      });

      // 3. Confirm upload
      await confirmDocumentUpload(clientId, {
        gcsPath,
        fileName: selectedFile.name,
        mimeType: selectedFile.type,
        fileSize: selectedFile.size,
        documentType,
        documentLabel: documentType === "other" ? documentLabel : undefined,
      });

      // 4. Refresh
      setSelectedFile(null);
      setUploadProgress(0);
      setDocumentLabel("");
      setSuccess("Document uploaded successfully.");
      scrollToTop();
      await fetchDocuments();
      window.dispatchEvent(new Event("storage-changed"));
    } catch (err) {
      setError(err.message || "Upload failed.");
      scrollToTop();
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    try {
      await deleteClientDocument(clientId, documentId);
      setConfirmDeleteId(null);
      setSuccess("Document deleted.");
      scrollToTop();
      await fetchDocuments();
      window.dispatchEvent(new Event("storage-changed"));
    } catch (err) {
      setError(err.message || "Failed to delete document.");
      setConfirmDeleteId(null);
      scrollToTop();
    }
  };

  const handleDownload = async (url, fileName) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch {
      window.open(url, "_blank");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const percentUsed = storageLimit > 0 ? (storageUsed / storageLimit) * 100 : 0;
  const isNearLimit = percentUsed >= 90;

  if (loading) return <div style={{ color: t.textMuted, fontSize: 13, fontFamily: font.body, padding: 20 }}>Loading documents...</div>;

  return (
    <div ref={topRef} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── Success message ── */}
      {success && (
        <div style={{
          padding: "10px 14px", borderRadius: 8,
          background: t.emerald + "10", border: `1px solid ${t.emerald}30`,
          fontSize: 12, color: t.emerald, fontFamily: font.body,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          {success}
          <button onClick={() => setSuccess("")} style={{ background: "none", border: "none", cursor: "pointer", color: t.emerald, fontSize: 16, padding: 0 }}>&times;</button>
        </div>
      )}

      {/* ── Error message ── */}
      {error && (
        <div style={{
          padding: "10px 14px", borderRadius: 8,
          background: t.coral + "10", border: `1px solid ${t.coral}30`,
          fontSize: 12, color: t.coral, fontFamily: font.body,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          {error}
          <button onClick={() => setError("")} style={{ background: "none", border: "none", cursor: "pointer", color: t.coral, fontSize: 16, padding: 0 }}>&times;</button>
        </div>
      )}

      {/* ── Storage Usage Bar ── */}
      <Panel padding={20}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>Storage</div>
          <div style={{ fontSize: 12, color: isNearLimit ? t.gold : t.textMuted, fontFamily: font.mono, fontWeight: 600 }}>
            {formatBytes(storageUsed)} of {formatBytes(storageLimit)} used
          </div>
        </div>
        <div style={{ height: 4, borderRadius: 2, background: t.navy + "20", overflow: "hidden" }}>
          <div style={{
            height: "100%", borderRadius: 2,
            width: `${Math.min(percentUsed, 100)}%`,
            background: isNearLimit ? t.gold : t.emerald,
            transition: "width 0.3s ease",
          }} />
        </div>
        {isNearLimit && (
          <div style={{ fontSize: 11, color: t.gold, fontFamily: font.body, marginTop: 6 }}>
            Storage is almost full. Delete old documents or upgrade your plan.
          </div>
        )}
      </Panel>

      {/* ── Upload Area ── */}
      <Panel padding={20}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>Upload Document</div>

        {/* Document type chips */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, fontWeight: 600, marginBottom: 6 }}>Document Type</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {DOCUMENT_TYPES.map(dt => {
              const active = documentType === dt.value;
              return (
                <button key={dt.value}
                  onClick={() => { setDocumentType(dt.value); if (dt.value !== "other") setDocumentLabel(""); }}
                  style={{
                    padding: "6px 14px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                    fontWeight: active ? 700 : 500, fontFamily: font.body, transition: "all 0.15s",
                    background: active ? t.emerald + "15" : t.surface,
                    color: active ? t.emerald : t.textSecondary,
                    border: `1.5px solid ${active ? t.emerald + "50" : t.border}`,
                  }}
                >
                  {TYPE_ICONS[dt.value]} {dt.label}
                </button>
              );
            })}
          </div>
          {documentType === "other" && (
            <input
              type="text"
              placeholder="e.g., Warranty Deed, HOA Docs, Appraisal..."
              value={documentLabel}
              onChange={e => setDocumentLabel(e.target.value)}
              maxLength={100}
              style={{
                marginTop: 8, width: "100%", boxSizing: "border-box", background: t.surfaceWarm,
                border: `1.5px solid ${t.emerald}40`, borderRadius: 8, padding: "10px 12px",
                color: t.textPrimary, fontSize: 13, outline: "none", fontFamily: font.body,
              }}
            />
          )}
        </div>

        {/* Drop zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? t.emerald : t.border}`,
            borderRadius: 10, padding: "28px 20px", textAlign: "center",
            cursor: uploading ? "default" : "pointer",
            background: dragOver ? t.emerald + "08" : t.surfaceWarm,
            transition: "all 0.2s",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_MIMES.join(",")}
            style={{ display: "none" }}
            onChange={e => { if (e.target.files?.[0]) handleFileSelect(e.target.files[0]); }}
          />

          {!selectedFile ? (
            <>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4, marginBottom: 8 }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <div style={{ fontSize: 13, color: t.textSecondary, fontFamily: font.body }}>
                Drag & drop a file here, or click to browse
              </div>
              <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginTop: 4 }}>
                PDF, JPEG, PNG, WebP, Word &middot; Max {formatBytes(MAX_FILE_SIZE)}
              </div>
            </>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "center" }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={t.emerald} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
              </svg>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>
                  {selectedFile.name.length > 40 ? selectedFile.name.slice(0, 37) + "..." : selectedFile.name}
                </div>
                <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono }}>
                  {formatBytes(selectedFile.size)}
                </div>
              </div>
              {!uploading && (
                <button
                  onClick={e => { e.stopPropagation(); setSelectedFile(null); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 16, padding: 4 }}
                >
                  &times;
                </button>
              )}
            </div>
          )}
        </div>

        {/* Progress bar */}
        {uploading && (
          <div style={{ marginTop: 12 }}>
            <div style={{ height: 4, borderRadius: 2, background: t.navy + "20", overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 2, background: t.emerald,
                width: `${uploadProgress}%`, transition: "width 0.2s ease",
              }} />
            </div>
            <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono, marginTop: 4, textAlign: "center" }}>
              Uploading... {uploadProgress}%
            </div>
          </div>
        )}

        {/* Upload button */}
        {selectedFile && !uploading && (
          <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
            <Button variant="primary" size="sm" onClick={handleUpload}>
              Upload {DOCUMENT_TYPES.find(d => d.value === documentType)?.label || "Document"}
            </Button>
          </div>
        )}
      </Panel>

      {/* ── Document List ── */}
      <Panel padding={20}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
          Documents ({documents.length})
        </div>

        {documents.length === 0 ? (
          <div style={{ textAlign: "center", padding: "30px 0" }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3, marginBottom: 8 }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
            </svg>
            <div style={{ fontSize: 13, color: t.textSecondary, fontFamily: font.body }}>
              No documents yet
            </div>
            <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, marginTop: 2 }}>
              Upload a pre-approval or contract to get started
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {documents.map(doc => (
              <div key={doc.id} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 14px", borderRadius: 10,
                background: t.surfaceWarm, border: `1px solid ${t.border}`,
                transition: "border-color 0.15s",
              }}
                onMouseEnter={e => e.currentTarget.style.borderColor = t.emerald + "50"}
                onMouseLeave={e => e.currentTarget.style.borderColor = t.border}
              >
                {/* Icon */}
                <span style={{ fontSize: 20, flexShrink: 0 }}>
                  {TYPE_ICONS[doc.document_type] || TYPE_ICONS.other}
                </span>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {doc.original_name}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 3 }}>
                    <Badge variant="default" style={{ fontSize: 10 }}>
                      {doc.document_label || DOCUMENT_TYPES.find(d => d.value === doc.document_type)?.label || doc.document_type}
                    </Badge>
                    <span style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono }}>
                      {formatBytes(Number(doc.file_size_bytes))}
                    </span>
                    <span style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
                      {new Date(doc.uploaded_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                    </span>
                  </div>
                </div>

                {/* Actions: Preview, Download, Delete */}
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {/* Preview — opens in new tab */}
                  <button
                    onClick={() => window.open(doc.downloadUrl, "_blank")}
                    title="Preview"
                    style={{
                      width: 32, height: 32, borderRadius: 6, border: `1px solid ${t.border}`,
                      background: t.surface, cursor: "pointer", display: "flex",
                      alignItems: "center", justifyContent: "center", transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = t.sky + "15"; e.currentTarget.style.borderColor = t.sky + "40"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = t.surface; e.currentTarget.style.borderColor = t.border; }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.sky} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                  {/* Download — browser download */}
                  <button
                    onClick={() => handleDownload(doc.downloadUrl, doc.original_name)}
                    title="Download"
                    style={{
                      width: 32, height: 32, borderRadius: 6, border: `1px solid ${t.border}`,
                      background: t.surface, cursor: "pointer", display: "flex",
                      alignItems: "center", justifyContent: "center", transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = t.emerald + "15"; e.currentTarget.style.borderColor = t.emerald + "40"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = t.surface; e.currentTarget.style.borderColor = t.border; }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.emerald} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </button>
                  {/* Delete */}
                  <button
                    onClick={() => setConfirmDeleteId(doc.id)}
                    title="Delete"
                    style={{
                      width: 32, height: 32, borderRadius: 6, border: `1px solid ${t.border}`,
                      background: t.surface, cursor: "pointer", display: "flex",
                      alignItems: "center", justifyContent: "center", transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = t.coral + "15"; e.currentTarget.style.borderColor = t.coral + "40"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = t.surface; e.currentTarget.style.borderColor = t.border; }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.coral} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* Delete confirmation — rendered via portal to document.body */}
      {confirmDeleteId && (
        <DeleteModal
          onCancel={() => setConfirmDeleteId(null)}
          onConfirm={() => handleDelete(confirmDeleteId)}
        />
      )}
    </div>
  );
}
